import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CartSvg, LikeSvg, SearchSvg, UserSvg } from '../../svgFile';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CartSidebar from '../common/CartSidebar';
import Login from '../auth/Login';
import { sampleCartItems, useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/IsAuth';
import { useDispatch, useSelector } from 'react-redux';
import { setCategoryFilter, setSearch, setShouldShowLoader, updateFilter } from '../../store/slices/productSlice';
import { selectCartItems } from '../../store/slices/cartSlice';
import ProfileDropdown from '../common/ProfileDropdown';
import { callAPIWithoutAuth } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import { clearSliderError, fetchSliders } from '../../store/slices/sliderSlice';

const Header = () => {
  const cart = useCart(sampleCartItems);
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const searchInputRef = useRef(null);
  const { categories, data: categoryData, loading, error } = useSelector((state) => state.category)
  const reduxItems = useSelector(selectCartItems);
  const displayCategories = categoryData?.length ? categoryData : categories?.data || []
  const { slider, data: sliderData, loadingSlider, errorSlider } = useSelector((state) => state.slider)
  const displaySliders = sliderData?.length ? sliderData : slider?.data || []
  const isOnProductsPage = location.pathname === '/choose-item-option';
  useEffect(() => {
    dispatch(fetchSliders())
    return () => {
      dispatch(clearSliderError())
    }
  }, [dispatch])
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlQuery = searchParams.get('query');
    if (urlQuery) {
      setSearchQuery(decodeURIComponent(urlQuery));
      setShowSuggestions(false);
      dispatch(setSearch(decodeURIComponent(urlQuery)));
    }
  }, [location.search, dispatch]);
  useEffect(() => {
    if (isOnProductsPage) {
      setShowSuggestions(false);
    }
  }, [location.pathname]);
  const searchProducts = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      setLoader(true);
      const response = await callAPIWithoutAuth(
        apiUrls.productSearch,
        { search: query },
        "GET",
        {}
      );
      setLoader(false);
      if (response?.data?.status && response?.data?.data?.length > 0) {
        setSuggestions(response?.data?.data || []);
        if (isTyping) {
          setShowSuggestions(true);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching products:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [isTyping]);
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (searchQuery && searchQuery.trim().length > 0 && isTyping) {
        searchProducts(searchQuery);
      } else {
        setSuggestions([]);
        if (!isTyping) {
          setShowSuggestions(false);
        }
      }
    }, 300);
    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery, searchProducts, isTyping]);

  const handleCheckout = () => {
  };
  const handleCustomize = () => {
  };
  const handleLoginSuccess = (userData) => {
  };
  const openLogin = () => setIsLoginOpen(true);
  const closeLogin = () => setIsLoginOpen(false);
  const handleCategoryClick = (category) => {
      dispatch(setShouldShowLoader(true));
    dispatch(setCategoryFilter(category?.id));
    navigate('/choose-item-option')
    dispatch(updateFilter({
      filterType: 'categories',
      key: category?.id,
      value: true
    }));
  };
  const handleProtectedLinkClick = (e, path, state = {}) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      openLogin();
      return;
    }
    navigate(path, { state });
  };
  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    if (isOnProductsPage) {
      dispatch(setSearch(value));
    }
    setSearchQuery(value);
    setIsTyping(true);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      applySearch();
    }
  };
  const applySearch = () => {
    setIsTyping(false);
    setShowSuggestions(false);
    if (searchQuery.trim() === "") {
      dispatch(setSearch(""));
      if (isOnProductsPage) {
        navigate('/choose-item-option');
      } else {
        navigate('/choose-item-option');
      }
    } else {
      dispatch(setSearch(searchQuery));
      if (isOnProductsPage) {
        navigate(`/choose-item-option?query=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate(`/choose-item-option?query=${encodeURIComponent(searchQuery)}`);
      }
    }
  };
  const handleSuggestionClick = (product) => {
    setIsTyping(false);
    setShowSuggestions(false);
    setSuggestions([]);
    navigate(`/product-detail/${product?.id}`);
    setSearchQuery('');
  };
  const handleInputFocus = () => {
    if (searchQuery && searchQuery.trim().length > 0 && suggestions.length > 0) {
      setIsTyping(true);
      setShowSuggestions(true);
    }
  };
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsTyping(false);
      setShowSuggestions(false);
    }, 200);
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchWrapper = document.querySelector('.search-wrapper');
      if (searchWrapper && !searchWrapper.contains(event.target)) {
        setIsTyping(false);
        setShowSuggestions(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  const handleLogoClick = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsTyping(false);
  };
  const handleClearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    setIsTyping(false);
    if (isOnProductsPage) {
      dispatch(setSearch(""));
    }
  };
  const handleSearchClick = () => {
    if (searchQuery.trim() !== "") {
      applySearch();
    }
  };
  return (
    <>
      <header>
        <div className='header-top'>
          <div className="container big-container">
            {loadingSlider && (
              <div className="text-center my-5">
                <span className="visually-hidden">Loading...</span>
              </div>
            )}
            {displaySliders?.map((it, i) => (it?.position === "top_bar" && <p key={i} className='mb-0'>{it?.title}</p>))}
          </div>
        </div>
        <div className='header-main'>
          <div className='header-middle'>
            <div className="container big-container">
              <div className="d-flex justify-content-between align-items-center">
                <Link to="/home" className="logo-sec" onClick={handleLogoClick}>
                  <img src="/image/logo.png" alt="" />
                </Link>
                <div className="search-wrapper d-none d-xl-block">
                  <div className="form-group position-relative">
                    <span className='input-icon' onClick={handleSearchClick} style={{ cursor: 'pointer' }}>
                      <SearchSvg />
                    </span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className='form-control'
                      placeholder='Search products...'
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        className="position-absolute bg-transparent border-0"
                        onClick={handleClearSearch}
                        style={{
                          right: '50px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          fontSize: '20px',
                          color: '#999',
                          zIndex: 1002
                        }}
                      >
                        ×
                      </button>
                    )}
                    {loader && isTyping && (
                      <div
                        className="position-absolute text-center py-2 bg-white border rounded"
                        style={{
                          width: "100%",
                          zIndex: 1001,
                          top: "100%",
                          left: 0,
                          color: 'black',
                          marginTop: "5px"
                        }}
                      >
                        Searching...
                      </div>
                    )}
                    {!loader && showSuggestions && isTyping && suggestions?.length > 0 && (
                      <ul
                        className="suggestion-dropdown list-unstyled p-2 shadow-sm"
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,

                          borderRadius: "6px",
                          zIndex: 1000,
                          maxHeight: "300px",
                          overflowY: "auto",
                          marginTop: "5px"
                        }}
                      >
                        {suggestions?.map((product, index) => (
                          <li key={index} className="mb-2">
                            <button
                              onClick={() => handleSuggestionClick(product)}
                              className="d-flex align-items-center text-decoration-none  p-3 rounded-3 w-100 border-0 "
                              style={{
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <img
                                src={product?.primary_image?.image_url || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                                alt={""}
                                width={48}
                                height={48}
                                className="rounded-circle me-3 object-fit-cover border border-2 border-white shadow-sm"
                                style={{
                                  objectFit: 'cover'
                                }}
                              />
                              <div className="flex-grow-1 text-start">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="fw-medium text-white">
                                    {product?.name || product?.title}
                                  </span>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center"
                                    >
                                      <span >
                                        <svg
                                          width={18}
                                          height={18}
                                          viewBox="0 0 18 18"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M11.655 8.4675L7.40999 4.23C7.34026 4.1597 7.25731 4.10391 7.16592 4.06583C7.07452 4.02775 6.9765 4.00815 6.87749 4.00815C6.77848 4.00815 6.68045 4.02775 6.58905 4.06583C6.49766 4.10391 6.41471 4.1597 6.34499 4.23C6.2053 4.37052 6.12689 4.56061 6.12689 4.75875C6.12689 4.95689 6.2053 5.14698 6.34499 5.2875L10.0575 9.0375L6.34499 12.75C6.2053 12.8905 6.12689 13.0806 6.12689 13.2787C6.12689 13.4769 6.2053 13.667 6.34499 13.8075C6.41445 13.8784 6.49728 13.9347 6.58869 13.9734C6.68009 14.012 6.77826 14.0321 6.87749 14.0325C6.97672 14.0321 7.07488 14.012 7.16629 13.9734C7.25769 13.9347 7.34053 13.8784 7.40999 13.8075L11.655 9.57C11.7311 9.49977 11.7919 9.41453 11.8334 9.31965C11.875 9.22478 11.8964 9.12232 11.8964 9.01875C11.8964 8.91517 11.875 8.81272 11.8334 8.71784C11.7919 8.62297 11.7311 8.53773 11.655 8.4675Z"
                                            fill="white"
                                          />
                                        </svg>
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {product?.price && (
                                  <p className="mb-0 text-primary fw-bold">
                                    ${product.price}
                                  </p>
                                )}
                                {product?.category && (
                                  <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                                    {product.category}
                                  </p>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="right-menu">
                  <div className='d-flex align-items-center gap-lg-4 gap-2 justify-content-end'>
                    <Link to='#'
                      className='header-menu'
                      onClick={(e) => handleProtectedLinkClick(e, '/dashboard', { activeTab: 'wishlist' })}>
                      <span className='header-menu-icon me-2'>
                        <LikeSvg />
                      </span>
                      <span className='d-none d-xl-inline-block'> My Favorites</span>
                    </Link>
                    <>
                      {!isLoggedIn ?
                        <button
                          className='header-menu border-0 bg-transparent p-0 position-relative'
                          onClick={openLogin}
                        >
                          <span className='header-menu-icon me-2'>
                            <UserSvg />
                          </span>
                          <span className='d-none d-xl-inline-block'>
                            Sign In </span>
                        </button>
                        :
                        <ProfileDropdown />
                      }
                    </>
                    <button
                      className='header-menu position-relative border-0 bg-transparent p-0'
                      onClick={!isLoggedIn ? openLogin : cart.openCart}
                    >
                      <span className='header-menu-icon me-3'>
                        <CartSvg />
                      </span>
                      <span className='d-none d-xl-inline-block'>    Cart</span>

                      {reduxItems?.length > 0 && (
                        <span className='cart-number'>{reduxItems?.length}</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='header-bottom'>
            <nav className="navbar navbar-expand-xl">
              <div className="container big-container">
                <div className='d-flex align-items-center justify-content-between w-100 d-xl-none'>
                  <div className="search-wrapper d-block d-xl-none">
                    <div className="form-group position-relative">
                      <span className='input-icon'><SearchSvg /></span>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className='form-control'
                        placeholder='Search products...'
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="position-absolute bg-transparent border-0"
                          onClick={handleClearSearch}
                          style={{
                            right: '50px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            fontSize: '20px',
                            color: '#999',
                            zIndex: 1002
                          }}
                        >
                          ×
                        </button>
                      )}
                      {loader && isTyping && (
                        <div
                          className="position-absolute text-center py-2 bg-white border rounded"
                          style={{
                            width: "100%",
                            zIndex: 1001,
                            top: "100%",
                            left: 0,
                            color: 'black',
                            marginTop: "5px"
                          }}
                        >
                          Searching...
                        </div>
                      )}
                      {!loader && showSuggestions && isTyping && suggestions?.length > 0 && (
                        <ul
                          className="suggestion-dropdown list-unstyled p-2 shadow-sm"
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            borderRadius: "6px",
                            zIndex: 1000,
                            maxHeight: "300px",
                            overflowY: "auto",
                            marginTop: "5px"
                          }}
                        >
                          {suggestions?.map((product, index) => (
                            <li key={index} className="mb-2">
                              <button
                                onClick={() => handleSuggestionClick(product)}
                                className="d-flex align-items-center text-decoration-none  p-3 rounded-3 w-100 border-0 "
                                style={{
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <img
                                  src={product?.primary_image?.image_url || 'https://cdn-icons-png.flaticon.com/512/149/149071.png'}
                                  alt={""}
                                  width={48}
                                  height={48}
                                  className="rounded-circle me-3 object-fit-cover border border-2 border-white shadow-sm"
                                  style={{
                                    objectFit: 'cover'
                                  }}
                                />
                                <div className="flex-grow-1 text-start">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="fw-medium text-white">
                                      {product?.name || product?.title}
                                    </span>
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                      >
                                        <span >
                                          <svg
                                            width={18}
                                            height={18}
                                            viewBox="0 0 18 18"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M11.655 8.4675L7.40999 4.23C7.34026 4.1597 7.25731 4.10391 7.16592 4.06583C7.07452 4.02775 6.9765 4.00815 6.87749 4.00815C6.77848 4.00815 6.68045 4.02775 6.58905 4.06583C6.49766 4.10391 6.41471 4.1597 6.34499 4.23C6.2053 4.37052 6.12689 4.56061 6.12689 4.75875C6.12689 4.95689 6.2053 5.14698 6.34499 5.2875L10.0575 9.0375L6.34499 12.75C6.2053 12.8905 6.12689 13.0806 6.12689 13.2787C6.12689 13.4769 6.2053 13.667 6.34499 13.8075C6.41445 13.8784 6.49728 13.9347 6.58869 13.9734C6.68009 14.012 6.77826 14.0321 6.87749 14.0325C6.97672 14.0321 7.07488 14.012 7.16629 13.9734C7.25769 13.9347 7.34053 13.8784 7.40999 13.8075L11.655 9.57C11.7311 9.49977 11.7919 9.41453 11.8334 9.31965C11.875 9.22478 11.8964 9.12232 11.8964 9.01875C11.8964 8.91517 11.875 8.81272 11.8334 8.71784C11.7919 8.62297 11.7311 8.53773 11.655 8.4675Z"
                                              fill="white"
                                            />
                                          </svg>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  {product?.price && (
                                    <p className="mb-0 text-primary fw-bold">
                                      ${product.price}
                                    </p>
                                  )}
                                  {product?.category && (
                                    <p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
                                      {product.category}
                                    </p>
                                  )}
                                </div>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  <button
                    className="navbar-toggler"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#navbarSupportedContent"
                    aria-controls="navbarSupportedContent"
                    aria-expanded="false"
                    aria-label="Toggle navigation"
                  >
                    <span className="navbar-toggler-icon" />
                  </button>
                </div>
                <div className="collapse navbar-collapse" id="navbarSupportedContent">
                  <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                    {displayCategories?.map((item, index) => (
                      <li key={index} className="nav-item">
                        <Link
                          className="nav-link"
                          to={"/"}
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick(item);
                          }}
                        >
                          {item?.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className='navbar-right'>
                    {displaySliders?.map((it, i) => (it?.position === "side_bar" && <p key={i} className='mb-0'> <span className='me-2'><img src={it?.image_url} alt="" /><span style={{ marginLeft: '10px', color: '#ffa127ff' }}>{it?.title}</span></span></p>))}
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>
      <Login
        isOpen={isLoginOpen}
        onClose={closeLogin}
        onLoginSuccess={handleLoginSuccess}
      />
      <CartSidebar
        items={cart?.items}
        isOpen={cart?.isCartOpen}
        onClose={cart?.closeCart}
        onRemoveItem={cart?.removeItem}
        onCheckout={handleCheckout}
        onCustomize={handleCustomize}
      />
    </>
  )
}

export default Header;